import { OrderStatus, ORDER_STATUS_LABELS } from '@jiaofa/constants'
import type {
  DashboardDataPoint,
  DashboardHotProductItem,
  DashboardOverview,
  DashboardStoreRankItem,
} from '@/api/dashboard'

/**
 * 工作台导出报表的数据构造。
 *
 * 拆成「构造 sheet 数据」与「写出文件」两步：前者是纯函数、可单测，
 * 后者只做 `XLSX.writeFile` 这唯一副作用。这样导出内容的正确性
 * 不需要跑浏览器或 mock 文件系统就能验证。
 */

/** 一个 sheet 的原始数据（首行是表头） */
export interface ReportSheet {
  name: string
  rows: Array<Array<string | number>>
}

/** 导出时的概览指标（与页面上的卡片一一对应，值已格式化为可读文本） */
export interface ReportSummaryInput {
  /** 页面顶部「数据更新于」的时间戳 */
  generatedAt: Date
  /** 统计卡片的标签与显示值，与页面上看到的完全一致 */
  stats: Array<{ label: string; value: string }>
}

export const REPORT_SHEET_NAMES = {
  summary: '经营概览',
  trend: '近7日营业趋势',
  status: '订单状态分布',
  storeRank: '门店业绩排行',
  hotProducts: '热销商品Top3',
} as const

function formatAmount(amountInFen: number): string {
  return (amountInFen / 100).toFixed(2)
}

/** Excel sheet 名不允许超过 31 字符、不允许含 `[]:*?/\`，这里统一做一次防御 */
export function sanitizeSheetName(name: string): string {
  return name.replace(/[[\]:*?/\\]/g, '_').slice(0, 31)
}

function buildSummarySheet(input: ReportSummaryInput): ReportSheet {
  return {
    name: REPORT_SHEET_NAMES.summary,
    rows: [
      ['指标', '数值'],
      ...input.stats.map((stat) => [stat.label, stat.value]),
      [],
      ['数据更新于', input.generatedAt.toLocaleString('zh-CN', { hour12: false })],
    ],
  }
}

function buildTrendSheet(trend: DashboardDataPoint[]): ReportSheet {
  return {
    name: REPORT_SHEET_NAMES.trend,
    rows: [['日期', '营业额(元)'], ...trend.map((item) => [item.label, formatAmount(item.value)])],
  }
}

function buildStatusSheet(statusDistribution: DashboardDataPoint[]): ReportSheet {
  return {
    name: REPORT_SHEET_NAMES.status,
    rows: [
      ['订单状态', '数量'],
      ...statusDistribution.map((item) => [
        ORDER_STATUS_LABELS[item.label as OrderStatus] ?? item.label,
        item.value,
      ]),
    ],
  }
}

function buildStoreRankSheet(storeRank: DashboardStoreRankItem[]): ReportSheet {
  return {
    name: REPORT_SHEET_NAMES.storeRank,
    rows: [
      ['排名', '门店', '订单数', '客单价(元)', '营收(元)'],
      ...storeRank.map((store, index) => [
        index + 1,
        store.storeName,
        store.orderCount,
        formatAmount(store.avgPrice),
        formatAmount(store.revenue),
      ]),
    ],
  }
}

function buildHotProductsSheet(hotProducts: DashboardHotProductItem[]): ReportSheet {
  return {
    name: REPORT_SHEET_NAMES.hotProducts,
    rows: [
      ['排名', '商品', '销量(杯)', '销售额(元)'],
      ...hotProducts.map((product, index) => [
        index + 1,
        product.productName,
        product.sales,
        formatAmount(product.salesAmount),
      ]),
    ],
  }
}

/**
 * 把工作台概览装配成 5 张 sheet。
 *
 * 顺序与页面上的视觉顺序一致（指标卡 → 趋势 → 分布 → 门店排行 → 热销），
 * 让导出的文件与用户刚看过的界面能对上。
 */
export function buildDashboardReport(
  overview: DashboardOverview,
  summary: ReportSummaryInput,
): ReportSheet[] {
  return [
    buildSummarySheet(summary),
    buildTrendSheet(overview.trend ?? []),
    buildStatusSheet(overview.statusDistribution ?? []),
    buildStoreRankSheet(overview.storeRank ?? []),
    buildHotProductsSheet(overview.hotProducts ?? []),
  ]
}

/** 导出文件名：`交发云咖-运营工作台-20261027-1230.xlsx` */
export function buildReportFileName(generatedAt: Date): string {
  const stamp = [
    generatedAt.getFullYear(),
    String(generatedAt.getMonth() + 1).padStart(2, '0'),
    String(generatedAt.getDate()).padStart(2, '0'),
    '-',
    String(generatedAt.getHours()).padStart(2, '0'),
    String(generatedAt.getMinutes()).padStart(2, '0'),
  ].join('')
  return `交发云咖-运营工作台-${stamp}.xlsx`
}
